import { BreakpointState } from "@angular/cdk/layout";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { Subscription, filter, Observable } from "rxjs";

import { ClickOnAssetEvent } from "@hermes/aphrodite/carousel";
import { IconButtonSize, MEDIUM } from "@hermes/aphrodite/icon-button";
import {
    ControlPosition,
    HIGH_POSITION,
    LOW_POSITION,
} from "@hermes/aphrodite/video";
import { ModelObject } from "@hermes/api-model-core";
import { Asset } from "@hermes/api-model-asset";
import { Product, ProductStock } from "@hermes/api-model-product";
import { Context, LOCALE } from "@hermes/app-core";
import { SeoLinks } from "@hermes/fragments/seo-links";
import { Locale } from "@hermes/locale";
import {
    ProductPageAssetService,
    ProductPageFacade,
    ProductPageVariantService,
} from "@hermes/states/product-page";
import { VtoFacade } from "@hermes/states/vto";
import { AssetMediaType } from "@hermes/utils-generic/constants";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

@Component({
    selector: "h-simple-product-page",
    templateUrl: "./simple-product-page.component.html",
    styleUrls: ["./simple-product-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimpleProductPageComponent implements OnInit, OnDestroy {
    public product: Product;

    // Indicates whether the video is in a playing in swiper
    public isPlayVideo: boolean = true;

    public carouselAssets$: Observable<Asset[]> =
        this.productPageAssetService.transformedAssets$;

    public isMobile: boolean;
    public isServer: boolean;
    public isVtoEnabled = false;
    public variantsRoutes: SeoLinks = [];
    public controlSize: IconButtonSize = MEDIUM;
    public lowPosition: ControlPosition = LOW_POSITION;
    public highPosition: ControlPosition = HIGH_POSITION;

    public resetToFirstSlide$ =
        this.productPageVariantService.resetToFirstSlide$;

    private readonly subscription = new Subscription();

    constructor(
        public readonly productPageFacade: ProductPageFacade,
        @Inject(LOCALE) private readonly locale: Locale,
        private readonly productPageAssetService: ProductPageAssetService,
        private readonly productPageVariantService: ProductPageVariantService,
        private readonly breakpointService: BreakpointService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly context: Context,
        private readonly vtoFacade: VtoFacade,
    ) {}

    public ngOnInit(): void {
        this.isServer = this.context.isInServerMode();

        this.subscription.add(
            this.productPageFacade.currentProduct$
                .pipe(filter(Boolean))
                .subscribe((product: Product) => {
                    this.product = ModelObject.fromJsonData(product, Product);
                    this.buildVariantsRoutes();
                    if (!this.product.stock) {
                        this.product.stock = new ProductStock();
                    }
                    this.isVtoEnabled =
                        (product.isVto || product.vtoType !== undefined) ??
                        false;
                    if (this.isVtoEnabled) {
                        this.vtoFacade.updateCurrentVtoType(product.vtoType);
                    }
                }),
        );

        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                this.breakpointService
                    .mediumBreakpointObserver()
                    .subscribe((value: BreakpointState) => {
                        this.isMobile = !value.matches;
                        this.changeDetector.detectChanges();
                    }),
            );
        }
    }

    public handleSlideChange(carouselCurrentIndex: number): void {
        this.productPageAssetService.sendAnalyticsTagForCarouselChangeImage(
            carouselCurrentIndex,
            this.product.templateType,
            this.product.assets,
        );
    }

    public onExpandToggle(event: { id: string; isExpanded: boolean }): void {
        this.productPageFacade.showMoreVariant({
            id: event.id,
            isExpanded: event.isExpanded,
        });
    }

    /**
     * When zoom status change: send analytics
     */
    public openZoomModal(clickOnAssetEvent: ClickOnAssetEvent): void {
        if (clickOnAssetEvent.assetType === AssetMediaType.Image) {
            this.productPageFacade.toggleZoom({
                zoomStatus: true,
                currentIndex: clickOnAssetEvent.carouselCurrentIndex,
                zoomAssets: this.product?.assets,
                isTopLevelProduct: true,
            });
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public buildVariantsRoutes(): void {
        this.product.variants?.volumes?.forEach((volume) => {
            this.variantsRoutes.push({
                path: `${this.locale.urlPrefix}/product/${volume.slug}-${volume.sku}/`,
                label: `${volume.slug}-${volume.sku}`,
            });
        });
    }
}
